import { accountingType } from '@/features/master-table/models';
import {
  amountDifferenceOperator,
  amountDifferenceUnit,
} from '@/features/project/_models/project-filter';
import { t } from '@/i18n';
import { DateRangeSchema, DayjsSchema, booleanStringType } from '@/utils';
import {
  fundSheetTypeEnum,
  implementationMethodEnum,
  projectCategoryEnum,
  reviewTypeEnum,
  sheetTypeEnum,
} from '@/utils/enum';
import { z } from 'zod';

export const candidateOfInspectionByExternalExpert = z.enum(['true', 'false']);
export const CandidateOfInspectionByExternalExpertLabelModifier = {
  true: t('is-candidate-of-inspection-by-external-expert'),
  false: t('is-not-candidate-of-inspection-by-external-expert'),
} as const satisfies Record<
  z.infer<typeof candidateOfInspectionByExternalExpert>,
  string
>;

export const amountSearchTypeEnum = z.enum(['threshold', 'trend']);
const _amountSearchTypeLabelModifier = {
  threshold: t('threshold'),
  trend: t('trend'),
} as const satisfies Record<z.infer<typeof amountSearchTypeEnum>, string>;

export const initialValueOfProjectSearchForm = {
  fiscalYear: 2024,
  sheetType: sheetTypeEnum.enum.RS,
  fundSheetType: fundSheetTypeEnum.enum.standard,
  skipAdministrativeReformTeamEvaluation: booleanStringType.enum.false,
  amountSearchConditions: [
    {
      amountSearchType: amountSearchTypeEnum.enum.threshold,
      amountSearchDiffAmountUnit: amountDifferenceUnit.enum.amount,
      amountSearchDiffAmount: null,
      amountSearchDiffOperator: null,
      amountSearchBaseYearWithBudgetType: null,
      amountSearchBaseYearWithBudgetTypeEnd: null,
    },
  ],
};

// 定義の順番がtagの並び順に影響
export const ProjectSearchSchema = z.object({
  fiscalYear: DayjsSchema(),
  sheetType: sheetTypeEnum,
  fundSheetType: fundSheetTypeEnum.nullable(),
  // テキスト検索のキーワード
  q: z.string().nullable(),
  isSearchTargetProjectName: z.boolean().nullable(),
  corporateNumber: z.string().nullable(),
  categories: projectCategoryEnum.array().nullable(),
  implementationMethods: implementationMethodEnum.array().nullable(),
  accountingType: accountingType.nullable(),
  accountingTypeIds: z.string().uuid().array().nullable(),
  skipAdministrativeReformTeamEvaluation: booleanStringType.nullable(),
  projectNumbers: z.string().nullable(),
  isPublished: booleanStringType.nullable(),
  period: DateRangeSchema.nullable(),
  lastImplementedFiscalYearPeriod: DateRangeSchema.nullish(),
  inspectionByExternalExpertReviewType: reviewTypeEnum.array().nullable(),
  isCandidateOfInspectionByExternalExpert:
    candidateOfInspectionByExternalExpert.nullable(),
  // 数式条件（配列化）
  amountSearchConditions: z
    .array(
      z.object({
        amountSearchType: amountSearchTypeEnum.nullable(),
        amountSearchDiffAmount: z.number().nullable(),
        amountSearchDiffAmountUnit: amountDifferenceUnit.nullable(),
        amountSearchDiffOperator: amountDifferenceOperator.nullable(),
        amountSearchBaseYearWithBudgetType: z
          .array(z.union([z.string(), z.number()]))
          .nullable(),
        amountSearchBaseYearWithBudgetTypeEnd: z
          .array(z.union([z.string(), z.number()]))
          .nullish(),
      }),
    )
    .nullable(),
  // cascader項目
  nestedOrganizationIds: z.string().uuid().array().array().nullable(),
  nestedPolicyIds: z.string().uuid().array().array().nullable(),
  nestedOtherResponsibleOrganizationIds: z
    .string()
    .uuid()
    .array()
    .array()
    .nullable(),
  nestedExpenditureBudgetPurposeIds: z
    .string()
    .uuid()
    .array()
    .array()
    .nullable(),
});

export type ProjectSearchFormType = z.infer<typeof ProjectSearchSchema>;

type ProjectSearchKeysType = keyof typeof ProjectSearchSchema.shape;
export const ProjectSearchKeys = Object.keys(
  ProjectSearchSchema.shape,
) as ProjectSearchKeysType[];
